// @flow
import React from 'react';
import get from 'lodash/get';
import toLower from 'lodash/toLower';
import classNames from 'classnames';

import styles from './Variants.module.css';
import type { BundledVariant, VariantDescriptor } from '../../../sharedTypes';

type Props = {|
  +variants: Array<BundledVariant>,
  +selectedDescriptors: Array<VariantDescriptor>,
  +updateSelectedVariant: (id: string) => void,
  +updateSelectedDescriptors: (descriptor: VariantDescriptor) => void,
  +context: string,
|};

const DESCRIPTOR_TYPE = {
  color: 'COLOR',
  word: 'WORD1',
};
const WHITE_COLOR = ['white', '#ffffff', '#fff'];

const Variants = (props: Props) => {
  const { variants, selectedDescriptors, updateSelectedDescriptors, context } =
    props;

  function isVariantDescriptorPresent(
    target: VariantDescriptor,
    variantDescriptors: Array<VariantDescriptor>
  ) {
    const targetDescriptor = target.descriptor;
    return (
      variantDescriptors.findIndex(
        (variantDescriptor) => variantDescriptor.descriptor === targetDescriptor
      ) !== -1
    );
  }

  const getSelectedColor = () =>
    selectedDescriptors.find(
      (descriptor) => descriptor.type === DESCRIPTOR_TYPE.color
    );

  // returns the word variant for the selected color
  const getWordVariant = (word) => {
    const { descriptor: color } = getSelectedColor();
    return variants.find((variant) => {
      const variantColor = variant.variantDescriptors.find(
        (descriptor) => descriptor.type === DESCRIPTOR_TYPE.color
      );
      const variantWord = variant.variantDescriptors.find(
        (descriptor) => descriptor.type === DESCRIPTOR_TYPE.word
      );
      return (
        variantColor?.descriptor === color && variantWord?.descriptor === word
      );
    });
  };

  // object that stores unique descriptor objects for each type of descriptor
  const variantDescriptorsByDescriptorType: {
    [key: string]: Array<VariantDescriptor>,
  } = variants.reduce((_variantDescriptorsByDescriptorType, variant) => {
    variant.variantDescriptors.forEach((variantDescriptor) => {
      // variantDescriptor is an object, descriptor is a string that indicates
      // the property of the variant (e.g. red, large, etc).
      // TODO: Improve naming. This would require a back-end change.
      const { type } = variantDescriptor;
      _variantDescriptorsByDescriptorType[type] =
        _variantDescriptorsByDescriptorType[type] || [];
      if (
        !isVariantDescriptorPresent(
          variantDescriptor,
          _variantDescriptorsByDescriptorType[type]
        )
      ) {
        _variantDescriptorsByDescriptorType[type].push(variantDescriptor);
      }
    });

    return _variantDescriptorsByDescriptorType;
  }, {});

  // Extracting the descriptor strings from the list of selected descriptors.
  const selectedDescriptorsStrings = selectedDescriptors.map((descriptor) => {
    return descriptor.descriptor;
  });

  const descriptorTypes = Object.keys(
    variantDescriptorsByDescriptorType
  ).sort();

  const areVariantsColorOnly =
    descriptorTypes.length === 1 &&
    variantDescriptorsByDescriptorType.hasOwnProperty(DESCRIPTOR_TYPE.color);

  // Here, a sold out color is a variant that is sold out and only has a color
  // descriptor type. We need this info for a specific sold out ui.
  let soldOutColors: Array<string> = [];
  if (areVariantsColorOnly) {
    soldOutColors = variants
      .filter((variant) => variant.soldOut)
      .map((soldOutVariant) =>
        get(soldOutVariant, 'variantDescriptors[0].descriptor')
      );
  }

  return (
    <div>
      {descriptorTypes.map((descriptorType) => {
        return (
          <div key={descriptorType} className={styles.variantsContainer}>
            <div className={classNames(styles.variants, styles[context])}>
              {variantDescriptorsByDescriptorType[descriptorType].map(
                (variantDescriptor) => {
                  // variantDescriptor is an object, descriptor is a string that indicates
                  // the property of the variant (e.g. red, large, etc).
                  // TODO: Improve naming. This would require a back-end change.
                  const { type, descriptor, value } = variantDescriptor;
                  const isSelected =
                    selectedDescriptorsStrings.includes(descriptor);
                  const isColorType = type === DESCRIPTOR_TYPE.color;
                  const isSoldOutColor =
                    isColorType && soldOutColors.includes(descriptor);
                  const isWhiteColor =
                    isColorType &&
                    (WHITE_COLOR.includes(toLower(value[0])) ||
                      (value[1] && WHITE_COLOR.includes(toLower(value[1]))));
                  const isMultipleAttribute = descriptorTypes.length === 2;
                  const wordVariant =
                    isMultipleAttribute &&
                    !isColorType &&
                    getWordVariant(descriptor);

                  return (
                    <React.Fragment key={descriptor}>
                      {isColorType ? (
                        <div className={styles.colorBoxWrapper}>
                          <button
                            type="button"
                            className={classNames(styles.colorBox, {
                              [styles.selected]: isSelected,
                              [styles.soldOutColor]: isSoldOutColor,
                              [styles.white]: isWhiteColor,
                            })}
                            onClick={() =>
                              updateSelectedDescriptors(variantDescriptor)
                            }
                          >
                            <span className="sr-only">
                              Select Variant: {descriptor}
                            </span>
                            <span
                              className={styles.color}
                              style={
                                value.length > 1
                                  ? {
                                      background: `linear-gradient(-45deg, ${value[1]}, ${value[1]} 49%, ${value[0]} 51%)`,
                                    }
                                  : {
                                      backgroundColor: value[0],
                                    }
                              }
                            />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className={classNames(styles.nonColorBox, {
                            [styles.selected]: isSelected,
                            [styles.word]: descriptor.length > 2,
                            [styles.wordSoldOut]: wordVariant?.soldOut,
                            [styles.wordNotAvailable]:
                              isMultipleAttribute && !wordVariant,
                          })}
                          onClick={() =>
                            updateSelectedDescriptors(variantDescriptor)
                          }
                        >
                          <span className="sr-only">
                            Select Variant: {descriptor}
                          </span>
                          {descriptor}
                        </button>
                      )}
                    </React.Fragment>
                  );
                }
              )}
            </div>
            <p className={styles.descriptor}>
              {(context === 'accordion' || context === 'pdp') &&
                `Selected Option: ${selectedDescriptors
                  .filter((selectedDescriptor) => {
                    return selectedDescriptor.type === descriptorType;
                  })
                  .map((a) => a.descriptor)
                  .toString()}`}
            </p>
          </div>
        );
      })}
    </div>
  );
};

export default Variants;
